/**
 * The weekly progress slider (T7.26, design.md § 10a, 7.4).
 *
 * It sits beside the week highlighter and answers a different question. The
 * highlighter asks "what is scheduled this week"; this asks "what did the
 * {blueprint} look like in this week", from the last published version
 * effective in it.
 *
 * T9.1-T9.3 changed what moving it does. It no longer only reads out a figure:
 * releasing the handle renders the SHEET as at that week, read-only, as a
 * preview over whatever is open underneath. Two consequences shape this file:
 *
 *   - The committed week is the ROUTE'S, carried in the URL, not state held
 *     here. That is what makes a previewed week shareable, makes Back work, and
 *     lets the version dropdown and this slider agree instead of fighting.
 *     `committedWeek` comes down; `onCommit` goes up.
 *   - The handle and the figures move DURING the drag; the sheet is fetched
 *     once, on release. So there is still local state for the live position,
 *     and it is committed on pointerup/keyup/touchend rather than on change.
 *     One request per gesture, not one per week crossed.
 *
 * Three week states, which must read differently and never be collapsed:
 *
 *   own version      the week has a version effective in it; its figure is the
 *                    week's figure.
 *   carried forward  no version of its own, so the previous week's figure stands.
 *                    It is shown, AND it is labelled as carried forward together
 *                    with the week it came from — an unlabelled carried figure
 *                    reads as a report that was never filed.
 *   blank            no version precedes the week at all. There is NO percentage
 *                    here. Rendering 0% would say the work had not started, when
 *                    what actually happened is that nothing was reported yet.
 *
 * Nothing in this file computes a percentage; every figure comes from the API,
 * which computed it with the shared `buildModel` and the state scale that
 * version was published with.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTerms } from '../../lib/terms';
import {
  indexOfVersionWeek,
  indexOfWeek,
  weeksToDate,
  type WeekSeries,
  type WeekSeriesRow,
} from '../../lib/weekSeries';
import { dayFmt, mondayOf, ymd } from './weeks';

/** "Week of 14 Sep – 18 Sep 2026", the same shape the week highlighter uses. */
export function weekLabel(row: WeekSeriesRow): string {
  return `Week of ${dayFmt(row.start)} – ${dayFmt(row.end)} ${row.end.slice(0, 4)}`;
}

/**
 * The week a carried-forward figure was actually published into: the most
 * recent earlier week in the series that holds the same version as its own.
 *
 * Read from the series rather than from `effectiveAt`. A backdated version's
 * `effectiveAt` is the last instant of its week in UTC, so converting it back
 * to a local Monday lands a week late in any zone east of UTC — in SAST it
 * reported a version published for 14 September as published for the 21st.
 * The series already says which week owns the version; there is nothing to
 * recompute.
 */
function sourceWeekOf(weeks: WeekSeriesRow[], index: number): string | null {
  const versionId = weeks[index]?.versionId;
  if (!versionId) return null;
  for (let i = index - 1; i >= 0; i--) {
    const w = weeks[i];
    if (w && !w.carriedForward && w.versionId === versionId) return w.start;
  }
  return null;
}

/**
 * What a screen reader hears as the slider moves. It carries the same three
 * cases as the visible readout, because the distinction between blank and zero
 * is exactly the one a bare number would destroy.
 */
function valueText(row: WeekSeriesRow, blueprintNoun: string): string {
  if (row.overallPct === null) return `${weekLabel(row)}: not reported`;
  const carried = row.carriedForward ? ', carried forward' : '';
  return `${weekLabel(row)}: ${row.overallPct}% of the ${blueprintNoun} complete${carried}`;
}

export function ProgressBar({
  series,
  committedWeek,
  committedVersionId,
  onCommit,
}: {
  series: WeekSeries;
  /** The week the ROUTE is rendering, from `?week=`. Null when nothing is previewed. */
  committedWeek: string | null;
  /** The version the address names, if any — see `committedIndex` for why. */
  committedVersionId: string | null;
  /** Called once per gesture, on release, with the Monday of the chosen week. */
  onCommit: (weekStart: string) => void;
}) {
  const terms = useTerms();
  const blueprint = terms.noun('blueprint').toLowerCase();

  const weeks = useMemo(
    () => weeksToDate(series, ymd(mondayOf(new Date()))),
    [series],
  );

  /**
   * Where the slider SITS when nothing is being dragged, in priority order:
   *
   *   1. the previewed week, if the address names one;
   *   2. the week that owns the version the address names, so choosing a
   *      version in the dropdown leaves the slider on that version's week
   *      rather than the two disagreeing about what is on screen (T9.6);
   *   3. the week containing today, or the most recent week when the plan has
   *      ended — a plan that is over still opens on its end.
   *
   * Nothing is stored for this. It is derived from the URL every render, which
   * is what makes the browser's Back button move the slider.
   */
  const committedIndex = useMemo(() => {
    const byWeek = indexOfWeek(weeks, committedWeek);
    if (byWeek >= 0) return byWeek;
    const byVersion = indexOfVersionWeek(weeks, committedVersionId);
    if (byVersion >= 0) return byVersion;
    return series.currentWeekIndex ?? Math.max(0, weeks.length - 1);
  }, [weeks, committedWeek, committedVersionId, series.currentWeekIndex]);

  // The live position during a drag. It follows the committed one whenever that
  // changes from outside — a dropdown choice, a Back button, a publish that
  // lengthened the series — and leads it only between pointerdown and release.
  const [live, setLive] = useState(committedIndex);
  useEffect(() => {
    setLive(committedIndex);
  }, [committedIndex]);

  /**
   * One fetch per gesture (T9.3).
   *
   * `change` fires on every week crossed; this does not act on it. The commit
   * happens on release — pointerup, touchend, or keyup for the arrow keys — and
   * is suppressed when the handle came back to where it started, so a drag out
   * and back is not a navigation. The ref holds the value at release time
   * because `live` in a handler closure is a render behind on the keyboard path.
   */
  const liveRef = useRef(live);
  liveRef.current = Math.min(live, Math.max(0, weeks.length - 1));
  const commit = () => {
    const row = weeks[liveRef.current];
    if (!row || liveRef.current === committedIndex) return;
    onCommit(row.start);
  };

  if (!weeks.length) return null;

  const index = Math.min(live, weeks.length - 1);
  const row = weeks[index];
  if (!row) return null;

  const sourceWeek = row.carriedForward ? sourceWeekOf(weeks, index) : null;

  return (
    <div className="progressbar">
      <span className="lbl" id="progressbar-label">
        Weekly progress
      </span>
      <input
        type="range"
        min={0}
        max={weeks.length - 1}
        step={1}
        value={index}
        aria-labelledby="progressbar-label"
        aria-valuetext={valueText(row, blueprint)}
        onChange={(e) => setLive(Number(e.target.value))}
        onPointerUp={commit}
        onTouchEnd={commit}
        onKeyUp={commit}
      />
      <span className="readout" aria-live="polite">
        <b>{weekLabel(row)}</b>
        {row.overallPct === null ? (
          <span className="pb-figure">
            <span className="pb-blank">Not reported</span>
            <span className="pb-note">
              No version of this {blueprint} had been published by this week.
            </span>
          </span>
        ) : (
          <span className="pb-figure">
            <span className={`pb-pct${row.carriedForward ? ' carried' : ''}`}>
              {row.overallPct}%
            </span>
            {/* A carried figure must be VISIBLY different from a reported one,
                not merely differently worded: read at a glance, two identical
                figures assert that something was reported in a week when
                nothing was. Hence the badge beside the number as well as the
                sentence under it, and the dotted rule under the figure itself. */}
            {row.carriedForward ? <span className="pb-carried">carried forward</span> : null}
            <span className="pb-note">
              {row.carriedForward ? (
                <>
                  No version was published in this week. This is version {row.versionNo}'s figure
                  {sourceWeek ? `, published for the week of ${dayFmt(sourceWeek)}` : ''}, standing
                  until the next one.
                </>
              ) : (
                <>
                  Version {row.versionNo}
                  {row.label ? ` — ${row.label}` : ''}.
                </>
              )}
            </span>
          </span>
        )}
      </span>

      {row.rooms.length ? (
        <span className="pb-rooms" aria-label={`${terms.plural('room')} in this week`}>
          {row.rooms.map((r) => (
            <span className="pb-room" key={r.RoomID} title={`${r.RoomID} ${r.RoomName}`}>
              {r.RoomID} <b>{r.pct}%</b>
            </span>
          ))}
        </span>
      ) : null}
    </div>
  );
}
